import { useCallback } from "react";
import { GlossaryLocalePair, Locale } from "../lib";
import { usePostDeepl } from "../services";

interface CreateGlossaryRequestBody {
    name: string;
    dictionaries: {
        source_lang: Locale;
        target_lang: Locale;
        entries: string;
        entries_format: "csv";
    }[];
}

export const useCreateGlossary = () => {
    const { postDeepl } = usePostDeepl();

    const createGlossary = useCallback(
        (name: string, file: File, locales: GlossaryLocalePair[]): Promise<void> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = async () => {
                    try {
                        const fileContent = reader.result as string;

                        const requestBody = {
                            name,
                            dictionaries: locales.map(pair => ({
                                source_lang: pair.source,
                                target_lang: pair.target,
                                entries: fileContent,
                                entries_format: "csv",
                            })),
                        } satisfies CreateGlossaryRequestBody;

                        const response = await postDeepl(
                            "glossaries",
                            JSON.stringify(requestBody),
                            "v3"
                        );

                        if (!response || !response.glossary_id) {
                            throw new Error("Error creating glossary");
                        }

                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                };

                reader.onerror = error => reject(error);

                reader.readAsText(file);
            });
        },
        [postDeepl]
    );

    return { createGlossary };
};
